import * as React from 'react';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import type { CommissionStatementMapping } from '../../../MedicareProAPI/Entities/CommissionStatement/CommissionStatementMapping';
import type { GlobCompanies } from '../../../MedicareProAPI/Entities/Glob/GlobCompanies';
import { UserContext } from '../../Context/UserContext';
import { GlobCompaniesRepository } from '../../../MedicareProAPI/Repositories/Glob/GlobCompaniesRepository';
import { MappingBuilder } from './MappingBuilder';
import type {
  CommissionStatementMappingData,
  CommissionStatementMappingSchema,
} from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import {
  CommissionStatementMappingDuplicateType,
  CommissionStatementMappingFieldName,
  CommissionStatementMappingRepository,
} from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import type { CommissionStatement } from '../../../MedicareProAPI/Entities/CommissionStatement/CommissionStatement';
import { SpreadsheetMapper } from './SpreadsheetMapper';
import { Range } from '../../Utilities/Range';
import { CommissionStatementMappingCompanyCollection } from '../../../MedicareProAPI/Collections/CommissionStatement/CommissionStatementMappingCompanyCollection';
import { CommissionStatementMappingCompany } from '../../../MedicareProAPI/Entities/CommissionStatement/CommissionStatementMappingCompany';
import StyledModal from '../../Components/StyledModal';
import type { CommissionStatementMappingCompanySchema } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingCompanyRepository';
import { CommissionStatementMappingCompanyRepository } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingCompanyRepository';
import { ListFormatter } from '../../Utilities/ListFormatter';

/**
 * Props interface for the CommissionStatementMappingForm component
 */
export interface CommissionStatementMappingFormProps {
  /** The commission statement mapping to edit */
  commissionStatementMapping: CommissionStatementMapping;

  /** Optional commission statement for data reference */
  commissionStatement?: CommissionStatement;

  /** The user performing the mapping */
  user: User;

  /** Type of form to display (default: FULL) */
  formType?: CommissionStatementMappingFormType;

  /** Callback fired when mapping is saved */
  onSave?: (commissionStatementMapping: CommissionStatementMapping) => void;

  /** Callback fired when mapping is deleted */
  onDelete?: () => void;
}

/**
 * Enumeration of mapping types available
 */
export enum CommissionStatementMappingType {
  /** Visual spreadsheet-based mapping interface */
  SPREADSHEET = 'spreadsheet',

  /** Form-based mapping builder interface */
  BUILDER = 'builder',
}

/**
 * Enumeration of form display types
 */
export enum CommissionStatementMappingFormType {
  /** Complete form with all options */
  FULL = 'full',

  /** Mapping-only interface */
  MAPPING = 'mapping',
}

/**
 * Commission Statement Mapping Form Component
 *
 * A comprehensive form component for creating and editing commission statement mappings.
 * Provides interfaces for both spreadsheet-based and builder-based mapping configuration,
 * along with company mapping management and duplicate handling options.
 *
 * @param props - The component props
 * @returns JSX.Element - The rendered mapping form component
 */
export const CommissionStatementMappingForm = (
  props: CommissionStatementMappingFormProps
): React.JSX.Element => {
  // Context and basic state
  const userData = React.useContext(UserContext);
  const [formType, setFormType] = React.useState<CommissionStatementMappingFormType>(
    props.formType || CommissionStatementMappingFormType.FULL
  );

  // Error and UI state
  const [formError, setFormError] = React.useState<string>();
  const [formMappingType, setFormMappingType] = React.useState<CommissionStatementMappingType>(
    CommissionStatementMappingType.SPREADSHEET
  );

  // Form data state
  const [commissionStatementMapping, setFormCommissionStatementMapping] =
    React.useState<CommissionStatementMapping>();
  const [commissionStatement, setFormCommissionStatement] = React.useState<CommissionStatement>();
  const [formId, setFormId] = React.useState<number>();
  const [formProviderName, setFormProviderName] = React.useState<string>();
  const [formDuplicateType, setFormDuplicateType] =
    React.useState<CommissionStatementMappingDuplicateType>();
  const [formCompanies, setFormCompanies] =
    React.useState<Array<CommissionStatementMappingCompany>>();
  const [formParent, setFormParent] = React.useState<CommissionStatementMapping>();
  const [formData, setFormData] = React.useState<Array<CommissionStatementMappingData>>();
  const [formIgnoredRows, setFormIgnoredRows] = React.useState<Array<number>>();
  const [formHeaderRow, setFormHeaderRow] = React.useState<number>();

  // Loading and processing state
  const [formDeleting, setFormDeleting] = React.useState<boolean>(false);
  const [formLoading, setFormLoading] = React.useState<boolean>(false);
  const [formProcessing, setFormProcessing] = React.useState<boolean>(false);

  // Modal and companies state
  const [showCompaniesModal, setShowCompaniesModal] = React.useState<boolean>(false);
  const [companiesFormLoading, setCompaniesFormLoading] = React.useState<boolean>(false);
  const [companies, setCompanies] = React.useState<Array<GlobCompanies>>();
  const [companiesLoading, setCompaniesLoading] = React.useState<boolean>(false);

  // Repository instances
  const companiesRepository = new GlobCompaniesRepository();
  companiesRepository.setAccessToken(userData.userAccessToken);

  const mappingRepository = new CommissionStatementMappingRepository();
  mappingRepository.setAccessToken(userData.userAccessToken);

  const mappingCompanyRepository = new CommissionStatementMappingCompanyRepository();
  mappingCompanyRepository.setAccessToken(userData.userAccessToken);

  // Statement data state
  const [formStatementData, setFormStatementData] = React.useState<Array<Array<any>>>();
  const [formStatementDataFormatted, setFormStatementDataFormatted] =
    React.useState<Array<Array<any>>>();

  /**
   * Effect hook to handle commission statement data updates
   * Sets the raw and formatted data when the commission statement prop changes
   */
  React.useEffect(() => {
    if (props.commissionStatement) {
      setFormStatementData(props.commissionStatement.getData());
      setFormStatementDataFormatted(props.commissionStatement.getDataFormatted());
    } else {
      setFormStatementData(undefined);
      setFormStatementDataFormatted(undefined);
    }
  }, [props.commissionStatement]);

  /**
   * Effect hook to handle commission statement mapping updates
   * Populates form fields with mapping data and handles company mapping logic
   */
  React.useEffect(() => {
    if (props.commissionStatementMapping) {
      // Set basic mapping properties
      setFormId(props.commissionStatementMapping.getId());
      setFormProviderName(props.commissionStatementMapping.getProviderName());
      setFormDuplicateType(props.commissionStatementMapping.getDuplicateType());
      setFormData(props.commissionStatementMapping.getData());
      setFormIgnoredRows(props.commissionStatementMapping.getIgnoredRows());
      setFormHeaderRow(props.commissionStatementMapping.getHeaderRow());

      // Handle company mappings - check if there are existing mappings
      let companies = props.commissionStatementMapping
        .getCommissionStatementMappingCompanies()
        .getItems();

      // If no existing company mappings, try to create them from aggregated data
      if (!companies || companies.length === 0) {
        const aggregatedNames = props.commissionStatement?.getAggregatedData(
          CommissionStatementMappingFieldName.CompanyName
        );

        if (aggregatedNames) {
          companies = Array.from(aggregatedNames, (name) => {
            const company = new CommissionStatementMappingCompany();
            company.setReferencedValues([name]);
            return company;
          });
        }
      }

      setFormCompanies(companies);
    } else {
      // Reset all form fields when no mapping is provided
      setFormId(undefined);
      setFormProviderName(undefined);
      setFormDuplicateType(undefined);
      setFormCompanies(undefined);
      setFormData(undefined);
      setFormIgnoredRows(undefined);
      setFormHeaderRow(undefined);
    }
  }, [props.commissionStatementMapping]);

  /**
   * Effect hook to sync local commission statement mapping state with props
   */
  React.useEffect(() => {
    setFormCommissionStatementMapping(props.commissionStatementMapping);
  }, [props.commissionStatementMapping]);

  /**
   * Effect hook to sync local commission statement state with props
   */
  React.useEffect(() => {
    setFormCommissionStatement(props.commissionStatement);
  }, [props.commissionStatement]);

  /**
   * Effect hook to load companies on component mount
   */
  React.useEffect(() => {
    loadCompanies();
  }, []);

  /**
   * Saves the company mappings for the current commission statement mapping
   *
   * @param commissionStatementMapping - The commission statement mapping to save companies for
   * @returns Promise that resolves when companies are saved
   */
  const handleSaveCompanies = (commissionStatementMapping: CommissionStatementMapping) => {
    // Transform form companies into API payload format
    const companiesMappingPayload: Array<CommissionStatementMappingCompanySchema> = Array.from(
      formCompanies,
      (formCompany) => ({
        referencedValues: formCompany.getReferencedValues(),
        company: formCompany.getCompany(),
      })
    );

    return mappingCompanyRepository.updateCommissionStatementMappingCompanies(
      commissionStatementMapping.getUser().getUserId(),
      commissionStatementMapping.getId(),
      companiesMappingPayload
    );
  };

  /**
   * Loads the list of available companies from the repository
   *
   * @returns Promise resolving to the array of companies
   */
  const loadCompanies = (): Promise<Array<GlobCompanies>> => {
    setCompaniesLoading(true);

    return companiesRepository.getGlobCompanies({ limit: 100000 }).then((mappingCollection) => {
      const newMappings = mappingCollection.getItems();
      setCompanies(newMappings);
      setCompaniesLoading(false);

      return newMappings;
    });
  };

  /**
   * Handle changing the selected Company
   *
   * @param event - Change event from the select element
   * @param index - Position in the array of companies (null for new company)
   */
  const handleChangeCompany = (
    event: React.ChangeEvent<HTMLSelectElement>,
    index: number = null
  ) => {
    const companyId = parseInt(event.target.value);
    const selectedCompany = companyId
      ? companies.find((company) => company.getCompanyId() === companyId)
      : null;

    let newFormCompanies;

    if (index === null) {
      // Creating a new company mapping
      const newFormCompany = new CommissionStatementMappingCompany();
      newFormCompany.setCompany(selectedCompany);

      newFormCompanies = [newFormCompany];
    } else {
      // Updating existing company mapping
      newFormCompanies = [...formCompanies];
      const selectedCompanyMapping = newFormCompanies[index];
      selectedCompanyMapping.setCompany(selectedCompany);
    }

    setFormCompanies(newFormCompanies);
  };

  /**
   * Handles the deletion of a commission statement mapping
   *
   * @param commissionStatementMapping - The mapping to delete
   */
  const handleDelete = (commissionStatementMapping: CommissionStatementMapping): void => {
    setFormDeleting(true);

    mappingRepository
      .deleteCommissionStatementMapping(
        commissionStatementMapping.getUser().getUserId(),
        commissionStatementMapping.getId()
      )
      .then(() => {
        if (props.onDelete) {
          setFormDeleting(false);
          props.onDelete();
        }
      });
  };

  /**
   * Handles the creation or updating of a CommissionStatementMapping
   *
   * @param mapping - CommissionStatementMapping to update or create
   * @returns Promise resolving on the CommissionStatementMapping being updated/created
   */
  const handleSave = (
    mapping: CommissionStatementMapping
  ): Promise<CommissionStatementMapping | Error> => {
    setFormLoading(true);
    setFormError(null);

    // Prepare company collection
    const commissionStatementMappingCompanyCollection =
      new CommissionStatementMappingCompanyCollection();

    if (formCompanies) {
      commissionStatementMappingCompanyCollection.setItems(formCompanies);
    }

    // Update the mapping object with form data
    mapping
      .setProviderName(formProviderName)
      .setDuplicateType(formDuplicateType)
      .setParent(formParent)
      .setData(formData)
      .setIgnoredRows(formIgnoredRows)
      .setHeaderRow(formHeaderRow)
      .setCommissionStatementMappingCompanies(commissionStatementMappingCompanyCollection);

    // Prepare API payload
    const mappingData: CommissionStatementMappingSchema = {
      providerName: mapping.getProviderName(),
      data: mapping.getData(),
      duplicateType: mapping.getDuplicateType(),
      parent: mapping.getParent(),
      isShared: mapping.getIsShared(),
      ignoredRows: mapping.getIgnoredRows(),
      headerRow: mapping.getHeaderRow(),
    };

    let mappingResponse;

    // Determine whether to create or update the mapping
    if (mapping.getId()) {
      mappingResponse = mappingRepository.updateCommissionStatementMapping(
        mapping.getUser().getUserId(),
        mapping.getId(),
        mappingData
      );
    } else {
      mappingResponse = mappingRepository.createCommissionStatementMapping(
        mapping.getUser().getUserId(),
        mappingData
      );
    }

    return mappingResponse
      .then((updatedMapping) => {
        setFormCommissionStatementMapping(updatedMapping);

        if (props.onSave) {
          props.onSave(updatedMapping);
        }

        handleSaveCompanies(updatedMapping);

        return updatedMapping;
      })
      .catch((e: Error) => {
        setFormError('Please ensure all fields have been completed');
        return e;
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  /**
   * Checks if the companies mapping is disabled
   * Returns true if no companies are selected or any company is missing
   *
   * @returns Boolean indicating if companies mapping should be disabled
   */
  const formCompaniesMappingDisabled = () => {
    if (!formCompanies || formCompanies.length === 0) return true;
    return formCompanies.some((company) => !company.getCompany());
  };

  /**
   * Gets the list of company names that have been selected
   *
   * @returns Array of company names or null if none selected
   */
  const formCompaniesWithCompany = (): Array<string> | null => {
    if (!formCompanies || formCompanies.length === 0) {
      return null;
    }

    return formCompanies
      .filter((mappingCompany) => mappingCompany.getCompany())
      .map((mappingCompany) => mappingCompany.getCompany().getCompanyName());
  };

  // Check if the form is complete and ready for submission
  const formComplete = formProviderName !== null && formProviderName !== undefined;

  return (
    <>
      {/* Companies Selection Modal */}
      <StyledModal
        size="lg"
        title="Select Companies"
        footerCloseButton={true}
        show={showCompaniesModal}
        onClose={(e) => setShowCompaniesModal(false)}
      >
        {/* Company mappings section - shown when company name field exists */}
        {commissionStatementMapping &&
          commissionStatementMapping.getColumnByFieldName(
            CommissionStatementMappingFieldName.CompanyName
          ) &&
          formCompanies &&
          formCompanies.length > 0 && (
            <>
              <div className="row">
                <div className="mb-2 col-6">Company</div>
                <div className="mb-2 col-6">Name in Sheet</div>
              </div>
              {formCompanies.map((companyMapping, companyMappingIndex) => {
                return (
                  <div key={companyMappingIndex} className="row">
                    <div className="mb-2 col-6">
                      {companies && (
                        <select
                          value={
                            companyMapping.getCompany()
                              ? companyMapping.getCompany().getCompanyId()
                              : undefined
                          }
                          onChange={(e) => handleChangeCompany(e, companyMappingIndex)}
                          className="form-control"
                        >
                          <option>Select a company</option>
                          {companies.map((company) => (
                            <option key={company.getCompanyId()} value={company.getCompanyId()}>
                              {company.getCompanyName()}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="mb-2 col-6">
                      <input
                        disabled={true}
                        type="text"
                        className="form-control border-secondary"
                        autoComplete="off"
                        value={ListFormatter.format(companyMapping.getReferencedValues())}
                      />
                    </div>
                  </div>
                );
              })}
            </>
          )}

        {/* Single company selection - shown when no company name field exists */}
        {commissionStatementMapping &&
          !commissionStatementMapping.getColumnByFieldName(
            CommissionStatementMappingFieldName.CompanyName
          ) && (
            <>
              <p>
                No "Company Name" field has been defined for this mapping, so we can assume all
                records in this mapping will be associated with the same company.
              </p>
              {companies && (
                <select
                  value={
                    commissionStatementMapping.getCommissionStatementMappingCompanies() &&
                    commissionStatementMapping
                      .getCommissionStatementMappingCompanies()
                      .getTotalItems() > 0 &&
                    commissionStatementMapping
                      .getCommissionStatementMappingCompanies()
                      .getItem(0)
                      .getCompany()
                      ? commissionStatementMapping
                          .getCommissionStatementMappingCompanies()
                          .getItem(0)
                          .getCompany()
                          .getCompanyId()
                      : undefined
                  }
                  onChange={(e) => handleChangeCompany(e)}
                  className="form-control"
                >
                  <option>Select a company</option>
                  {companies.map((company) => (
                    <option key={company.getCompanyId()} value={company.getCompanyId()}>
                      {company.getCompanyName()}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}

        {/* Modal action buttons */}
        <div className="row">
          <div className="mb-2 mt-2 col-12">
            <button
              className="btn btn-success"
              type="button"
              onClick={(e) => setShowCompaniesModal(false)}
              disabled={formCompaniesMappingDisabled() || companiesFormLoading}
            >
              {companiesFormLoading && (
                <>
                  <i className="fas fa-spinner fa-spin"></i>{' '}
                </>
              )}
              Update Changes
            </button>
          </div>
        </div>
      </StyledModal>

      {/* Main Form Content */}
      <div className="row">
        {/* Error Display */}
        {formError && (
          <div className="col-12 mb-3">
            <div className="alert alert-danger" role="alert">
              {formError}
            </div>
          </div>
        )}

        {/* Basic form fields - only shown for new mappings or full form type */}
        {(!formId || formType === CommissionStatementMappingFormType.FULL) && (
          <>
            {/* Mapping Name Field */}
            <div className="mb-2 col-12">
              <label className="label">Mapping Name</label>
              <div className="input-group">
                <input
                  disabled={formLoading}
                  type="text"
                  className="form-control border-secondary"
                  autoComplete="off"
                  value={formProviderName || ''}
                  onChange={(e) => setFormProviderName(e.target.value)}
                />
              </div>
            </div>

            {/* Duplicate Rows Handling */}
            <div className="mb-2 col-12">
              <label className="label">
                Duplicate Rows{' '}
                <small>
                  (If this document has multiple rows for some policies, let us know how to handle
                  them)
                </small>
              </label>
              <div className="input-group">
                <select
                  disabled={formLoading}
                  className="form-control border-secondary"
                  value={formDuplicateType}
                  onChange={(e) =>
                    setFormDuplicateType(
                      e.target.value === ''
                        ? undefined
                        : (e.target.value as CommissionStatementMappingDuplicateType)
                    )
                  }
                >
                  <option value="">Not Applicable</option>
                  <option value={CommissionStatementMappingDuplicateType.Duplicate}>
                    Duplicate - Multiple entries will be created with each commission amount
                  </option>
                  <option value={CommissionStatementMappingDuplicateType.Merge}>
                    Merge - Commission amount will be summed into a single entry
                  </option>
                </select>
              </div>
            </div>

            {/* Header Row Selection */}
            <div className="mb-2 col-12">
              <label className="label">Header Row</label>
              <div className="input-group">
                <select
                  disabled={formLoading}
                  className="form-control border-secondary"
                  value={formHeaderRow}
                  onChange={(e) =>
                    setFormHeaderRow(e.target.value === '' ? undefined : parseInt(e.target.value))
                  }
                >
                  <option value=""></option>
                  {
                    // Temporary solution
                    Range.getNumbers(10).map((field) => (
                      <option value={field} key={field}>
                        {field + 1}
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>
          </>
        )}
        <div className="mb-2 col-12">
          <label className="label">Company / Companies</label>
          {companiesLoading === true && (
            <p className="alert alert-info small">
              Loading... <i className="fas fa-spinner fa-spin"></i>
            </p>
          )}
          {companiesLoading === false && companies && companies.length > 0 && (
            <div>
              <button
                onClick={(e) => setShowCompaniesModal(true)}
                className="btn btn-secondary btn-sm mr-2"
                type="button"
                title="Choose companies associated with this statement"
              >
                {formCompaniesWithCompany()
                  ? ListFormatter.format(formCompaniesWithCompany())
                  : 'Select'}
              </button>
            </div>
          )}
        </div>
        {formStatementData && formStatementData.length > 0 && (
          <div className="col-12">
            <div className="mt-2 btn-group btn-group-tabbed btn-group-toggle" data-toggle="buttons">
              <label
                onClick={(e) => setFormMappingType(CommissionStatementMappingType.SPREADSHEET)}
                className={`btn-sm btn btn-primary${formMappingType === CommissionStatementMappingType.SPREADSHEET ? ' active' : ''}`}
              >
                Spreadsheet Preview
              </label>
              <label
                onClick={(e) => setFormMappingType(CommissionStatementMappingType.BUILDER)}
                className={`btn-sm btn btn-primary${formMappingType === CommissionStatementMappingType.BUILDER ? ' active' : ''}`}
              >
                Mapping Builder
              </label>
            </div>
          </div>
        )}
        <div className="mb-2 col-12">
          <div className="border border-secondary p-2">
            {/* Spreadsheet Mapper View */}
            {formMappingType === CommissionStatementMappingType.SPREADSHEET &&
              formStatementData &&
              formStatementData.length > 0 && (
                <>
                  <label className="label">
                    Field Mapping & Preview <small>(Click fields that should be ignored)</small>
                  </label>
                  <SpreadsheetMapper
                    data={formStatementData}
                    dataFormatted={formStatementDataFormatted}
                    value={formData}
                    ignoredRows={formIgnoredRows}
                    headerRow={formHeaderRow}
                    onDataChange={(newData) => {
                      setFormData(newData);
                      return newData;
                    }}
                    onIgnoredRowsChange={(newIgnoredRows) => {
                      setFormIgnoredRows(newIgnoredRows);
                      return newIgnoredRows;
                    }}
                    maxRows={20}
                  />
                </>
              )}

            {/* Mapping Builder View */}
            {(formMappingType === CommissionStatementMappingType.BUILDER ||
              !formStatementData ||
              (formStatementData && formStatementData.length === 0)) && (
              <>
                <label className="label">Field Mapping</label>
                <MappingBuilder
                  value={formData}
                  data={formStatementData}
                  headerRow={formHeaderRow}
                  onChange={(newData) => {
                    setFormData(newData);
                    return newData;
                  }}
                />
              </>
            )}
          </div>
        </div>
        {/* Action Buttons */}
        <div className="col-12 mb-2">
          {/* Save Button */}
          <button
            onClick={(e) => handleSave(commissionStatementMapping)}
            className="btn btn-success mr-2"
            type="button"
            title="Click to save your changes"
            disabled={formComplete === false}
          >
            {formLoading ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Saving
              </>
            ) : (
              <>
                <i className="fas fa-save" /> Save Changes
              </>
            )}
          </button>

          {/* Delete Button */}
          <button
            onClick={(e) => handleDelete(commissionStatementMapping)}
            className="btn btn-danger"
            type="button"
            title="Click to delete this mapping"
          >
            {formDeleting ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Deleting
              </>
            ) : (
              <>
                <i className="fas fa-trash" /> Delete
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};
